/* Copyright (c) 2025 MOCA Systems, Inc. All rights reserved. */

import { Injectable, inject } from '@angular/core';
import { filter, last, map, Observable, switchMap, take, tap, throwError } from 'rxjs';

import { FbdbRequests, ITakeSnapshotResult } from './fbdb-requests.service';
import { LoggerServiceFactory } from '../common/logging/LoggerServiceFactory';
import { AppState } from './app-state.service';

export enum SnapshotInitiation {
	SystemOnBulkUpdate = 'systemOnBulkUpdate',
}
@Injectable({
	providedIn: 'root',
})
export class FirebaseSnapshotsService {
	private logger = LoggerServiceFactory.create('FirebaseSnapshotsService');
	private appState: AppState = inject(AppState);
	private fbdbRequests = inject(FbdbRequests);

	/**
	 * Takes a snapshot of the current plan state for a given project and user.
	 *
	 * @param projectId - The unique identifier of the project.
	 * @param planId - The unique identifier of the plan within the project.
	 * @param extraParams - Optional additional parameters to include in the snapshot request.
	 * @param meta - Optional metadata to include in the snapshot.
	 * @returns An Observable that emits the result of the snapshot operation.
	 */
	public takeSnapshot$(
		projectId: string,
		planId: string,
		extraParams: Record<string, string> = {},
		meta: Record<string, string> = {}
	): Observable<ITakeSnapshotResult> {
		const missingParams: string[] = [];

		if (!projectId) missingParams.push('projectId');
		if (!planId) missingParams.push('planId');

		if (missingParams.length > 0) {
			const errorMsg = `takeSnapshot: Missing or invalid parameters: ${missingParams.join(', ')}`;
			this.logger.error(errorMsg);
			return throwError(() => new Error(errorMsg));
		}

		return this.appState.userId.pipe(
			filter((userId) => !!userId),
			take(1),
			map((userId) => ({
				projectId,
				planId,
				snapshot: {
					...meta,
					userId,
				},
				...extraParams,
			})),
			switchMap((params) => this.fbdbRequests.takeSnapshot(params, { disableDeletion: true }).pipe(last()))
		);
	}
}
